/**
 * Money in / money out (Settings): browser checkout, IBAN on-ramp, reserve
 * top-up from the card balance, and cashing investments back out to the card.
 * Live mode only, and only once a Gnosis Pay session exists.
 */
import { useEffect, useState } from "react";
import * as gp from "../lib/gnosispay/api";
import { GNOSIS_GAS } from "../lib/config";
import { activateIban, cashOutToCard, fundReserveFromCard, getIbanState, type IbanState } from "../lib/ramps";
import { loadOrCreateWallet } from "../lib/wallet";
import {
  beginLiveCardTopUp,
  maintainGasAccount,
  portfolioTotals,
  refreshLiveBalances,
  resumeLiveCardTopUp,
  useAppState,
} from "../lib/store";
import { formatPence } from "../lib/money";

export function MoneySection() {
  const s = useAppState();
  const [hasGp, setHasGp] = useState(false);
  const [iban, setIban] = useState<IbanState | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState("");
  const totals = portfolioTotals(s.roundups, s.settings.stockSymbol, s.settings.currency);

  useEffect(() => {
    void (async () => {
      // Self-healing sign-in: the device wallet IS the account, so re-SIWE
      // silently instead of hiding this whole section on token expiry.
      const session = await gp
        .ensureSession()
        .then(() => true)
        .catch(() => false);
      setHasGp(session);
      if (session) setIban(await getIbanState().catch(() => null));
    })();
  }, []);

  useEffect(() => {
    if (
      s.settings.demoMode ||
      !hasGp ||
      s.gasBootstrap?.status === "repaid" ||
      s.gasBootstrap?.status === "self_funded"
    ) {
      return;
    }
    const poll = () => {
      void refreshLiveBalances();
      void maintainGasAccount(setProgress);
    };
    poll();
    const timer = setInterval(poll, 15_000);
    return () => clearInterval(timer);
  }, [hasGp, s.gasBootstrap?.status, s.settings.demoMode]);

  useEffect(() => {
    if (
      s.settings.demoMode ||
      !hasGp ||
      !s.cardTopUp ||
      s.cardTopUp.status === "complete"
    ) {
      return;
    }
    const poll = () => {
      void refreshLiveBalances();
      void resumeLiveCardTopUp(setProgress);
    };
    poll();
    const timer = setInterval(poll, 15_000);
    return () => clearInterval(timer);
  }, [hasGp, s.cardTopUp?.status, s.settings.demoMode]);

  if (s.settings.demoMode || !hasGp) return null;

  const run = (name: string, fn: () => Promise<void>) => {
    setBusy(name);
    setError("");
    setProgress("");
    fn()
      .catch((err) => setError(String(err instanceof Error ? err.message : err)))
      .finally(() => {
        setBusy(null);
        void refreshLiveBalances();
      });
  };

  return (
    <>
      <h2>Money in</h2>
      <div className="card">
        <div className="balance-label" style={{ marginBottom: 8 }}>
          💳 Top up card
        </div>
        {!s.cardTopUp || s.cardTopUp.status === "complete" ? (
          <>
            <p className="sub" style={{ marginBottom: 10 }}>
              Pay in GBP through Ramp's browser checkout. Choose USDC.e on Gnosis Chain; Florin
              converts it into spendable {s.settings.currency === "GBP" ? "GBPe" : "card balance"}
              automatically. On the first top-up, up to about £1 is retained for long-term network
              processing; 0.02 xDAI (about 2p) returns the temporary advance and includes a one-time
              setup fee.
            </p>
            {s.cardTopUp?.status === "complete" && (
              <p className="sub" style={{ marginBottom: 10 }}>
                Your previous top-up reached the card ✓
              </p>
            )}
            <button
              className="btn-primary"
              disabled={busy !== null}
              onClick={() =>
                run("prepare-topup", async () => {
                  setProgress("Preparing your deposit address…");
                  await beginLiveCardTopUp();
                  setProgress("Deposit address ready");
                })
              }
            >
              {busy === "prepare-topup" ? "Preparing…" : "Start a top-up"}
            </button>
          </>
        ) : s.cardTopUp.status === "awaiting_deposit" ? (
          <>
            <p className="sub" style={{ marginBottom: 10 }}>
              Your address has been captured. At checkout select <strong>USDC.e</strong>, network
              <strong> Gnosis Chain</strong>, pay with GBP, and paste the copied address when asked.
            </p>
            <div className="row">
              <button
                className="btn-secondary"
                onClick={() => {
                  if (s.walletAddress) void navigator.clipboard.writeText(s.walletAddress);
                }}
              >
                Copy deposit address
              </button>
              <button
                className="btn-primary"
                onClick={() => {
                  if (s.walletAddress) void navigator.clipboard.writeText(s.walletAddress);
                  window.open("https://rampnetwork.com/buy-crypto", "_blank", "noopener,noreferrer");
                }}
              >
                Open GBP checkout
              </button>
            </div>
            <p className="sub" style={{ margin: "10px 0 0" }}>
              Return here after payment. Florin checks for the deposit automatically; you do not
              need to connect a separate wallet.
            </p>
          </>
        ) : s.cardTopUp.status === "failed" ? (
          <>
            <p className="sub" style={{ marginBottom: 10 }}>
              Your funds remain in your wallet. {s.cardTopUp.lastError}
            </p>
            <button
              className="btn-secondary"
              disabled={busy !== null}
              onClick={() => run("topup", () => resumeLiveCardTopUp(setProgress))}
            >
              Retry top-up
            </button>
          </>
        ) : (
          <p className="sub" style={{ margin: 0 }}>
            {s.cardTopUp.status === "processing_gas"
              ? "Deposit received — setting aside network costs and returning the advance and setup fee…"
              : `Converting the deposit into ${s.cardTopUp.safeTokenSymbol} card balance…`}
          </p>
        )}
      </div>

      <div className="card">
        <div className="balance-label" style={{ marginBottom: 8 }}>
          🏦 Top up by bank transfer
        </div>
        {iban?.details?.ibanStatus === "ASSIGNED" && iban.details.iban ? (
          <>
            <div className="kv">
              <span className="k">IBAN</span>
              <span className="v">{iban.details.iban}</span>
            </div>
            {iban.details.bic && (
              <div className="kv">
                <span className="k">BIC</span>
                <span className="v">{iban.details.bic}</span>
              </div>
            )}
            <p className="sub" style={{ margin: "8px 0 0" }}>
              Transfers from your bank arrive as card balance — usually within minutes.
            </p>
          </>
        ) : iban?.details && iban.details.ibanStatus !== "NOTSTARTED" ? (
          <p className="sub" style={{ margin: 0 }}>
            Your personal IBAN is being set up ({iban.details.ibanStatus.toLowerCase()}) — check
            back soon.
          </p>
        ) : iban?.available ? (
          <button
            className="btn-secondary"
            disabled={busy !== null}
            onClick={() =>
              run("iban", async () => {
                const wallet = await loadOrCreateWallet();
                const details = await activateIban(wallet);
                setIban({ available: true, details });
              })
            }
          >
            {busy === "iban" ? "Activating…" : "Get my personal IBAN"}
          </button>
        ) : (
          <p className="sub" style={{ margin: 0 }}>
            IBAN top-ups aren't available for your account yet.
          </p>
        )}
      </div>

      <div className="card">
        <div className="balance-label" style={{ marginBottom: 8 }}>
          ⚡ Automatic network setup
        </div>
        {s.gasBootstrap?.status === "repaid" ? (
          <p className="sub" style={{ margin: 0 }}>
            Ready — your first deposit funded the processing reserve and returned Florin's
            temporary advance and setup fee.
          </p>
        ) : s.gasBootstrap?.status === "self_funded" ? (
          <p className="sub" style={{ margin: 0 }}>
            Ready — this wallet already had enough network balance, so no advance was needed.
          </p>
        ) : s.gasBootstrap?.status === "waiting_deposit" ? (
          <p className="sub" style={{ margin: 0 }}>
            Ready for your first deposit. Florin has temporarily advanced the network cost; a
            small slice of the deposit will automatically repay it, cover the one-time setup fee,
            and fund future processing.
          </p>
        ) : s.gasBootstrap?.status === "refill_pending" ||
          s.gasBootstrap?.status === "repayment_pending" ? (
          <p className="sub" style={{ margin: 0 }}>
            Processing the network reserve from your first deposit — keep Florin open.
          </p>
        ) : s.gasBootstrap?.status === "failed" ? (
          <>
            <p className="sub" style={{ marginBottom: 10 }}>
              Automatic setup will retry safely. {s.gasBootstrap.lastError}
            </p>
            <button
              className="btn-secondary"
              disabled={busy !== null}
              onClick={() => run("gas", () => maintainGasAccount(setProgress))}
            >
              Retry setup
            </button>
          </>
        ) : (
          <p className="sub" style={{ margin: 0 }}>
            Florin is preparing this wallet automatically. No faucet or wallet connection is
            required.
          </p>
        )}
      </div>

      <div className="card">
        <div className="balance-label" style={{ marginBottom: 8 }}>
          💳 Move card balance into the reserve
        </div>
        <p className="sub" style={{ marginBottom: 10 }}>
          Pulls from your card balance (3-minute security delay) into your round-up reserve —
          conversion is automatic.
        </p>
        <div className="row">
          {[500, 1000, 2000].map((p) => (
            <button
              key={p}
              className="btn-secondary"
              disabled={busy !== null || (s.liveBalances && s.liveBalances.xdaiWei < GNOSIS_GAS.bootstrapMinWei)}
              onClick={() =>
                run("fund", async () => {
                  const wallet = await loadOrCreateWallet();
                  await fundReserveFromCard(wallet, p, setProgress);
                })
              }
            >
              {formatPence(p)}
            </button>
          ))}
        </div>
      </div>

      <h2>Money out</h2>
      <div className="card">
        <div className="balance-label" style={{ marginBottom: 8 }}>
          📤 Cash out investments
        </div>
        <p className="sub" style={{ marginBottom: 10 }}>
          Sells your {s.settings.stockSymbol}, brings the money back, and lands it as spendable
          card balance (from there, your IBAN can send it to a bank).
        </p>
        <button
          className="btn-secondary"
          disabled={busy !== null || totals.stockUnits === 0n}
          onClick={() => {
            if (!confirm(`Sell all your ${s.settings.stockSymbol} and send the proceeds to your card?`)) return;
            run("cashout", async () => {
              const wallet = await loadOrCreateWallet();
              await cashOutToCard(wallet, s.settings.stockSymbol, totals.stockUnits, setProgress);
            });
          }}
        >
          {busy === "cashout" ? "Cashing out…" : `Sell all ${s.settings.stockSymbol} → card`}
        </button>
      </div>

      {(busy || progress) && <div className="notice">{progress || "Working…"}</div>}
      {error && <div className="notice">{error}</div>}
    </>
  );
}
